import { useCallback, useEffect, useState } from 'react'
import type { GensDTO, OffreGensDTO, PersonneDTO } from '@whatremains/shared'
import { api, ErreurApi } from '../api'

/**
 * Les gens d'un lieu, et ce qu'ils proposent.
 *
 * L'écran doit tenir une ligne difficile : **montrer ce que les gens montrent,
 * et rien de plus.** La disposition est affichée parce qu'elle se lit sur un
 * visage ; l'intention ne l'est jamais, parce qu'elle ne se lit pas. Un écran
 * qui trierait les gens en « fiables » et « à éviter » détruirait tout le
 * système d'un seul coup.
 *
 * Donc : aucune couleur d'alerte sur une offre, aucun avertissement, aucun tri
 * par confiance. Le joueur décide avec ce qu'il voit, et il apprend en se
 * trompant. Ce qu'on affiche après coup, c'est un fait — « il prend, il te
 * salue, et il n'y a rien au bout » — jamais une conclusion.
 */

const NOMS_DISPOSITION: Record<PersonneDTO['disposition'], string> = {
  hostile: 'hostile',
  mefiant: 'méfiant',
  neutre: 'neutre',
  favorable: 'avenant',
  devoue: 'dévoué',
}

/*
 * Sans genre, et ce n'est pas un scrupule : le DTO n'a pas de champ de genre,
 * donc « Il sait quelque chose » se retrouvait sous le nom d'Ilde, qui pose les
 * mains sur une plaie et la referme. Vu à l'écran, pas dans le code.
 */
const NOMS_NATURE: Record<OffreGensDTO['nature'], string> = {
  renseignement: 'Sait quelque chose',
  objet: 'A quelque chose',
  service: 'Peut faire quelque chose',
  faveur: 'Ne veut pas d’or',
}

/*
 * Une phrase entière par cran, plutôt qu'un adjectif inséré dans un gabarit.
 * « On te reçoit en neutre ici » n'est pas du français, et c'est ce que la
 * version à trou produisait pour trois valeurs sur cinq.
 *
 * Aucune ne chiffre quoi que ce soit : le joueur lit comment on le regarde, pas
 * un score.
 */
const PHRASES_ACCUEIL: Record<GensDTO['accueil'], string> = {
  hostile: 'On te reçoit mal, ici.',
  mefiant: 'On te suit des yeux, ici.',
  neutre: 'On ne te regarde pas plus qu’un autre, ici.',
  favorable: 'On te reçoit bien, ici.',
  devoue: 'Ici, on te doit quelque chose.',
}

/**
 * De qui il est, et ce que ces gens-là font.
 *
 * Enzo, en jouant : « j'ai essayé de faire l'exercice de me dire, ce perso-là il
 * est affilié à quelle faction, et c'est absolument impossible sans contexte. »
 * Deux défauts se cumulaient sur cette seule ligne :
 *
 *   - le nom ne dit pas le métier. « Les Sourds » n'apprend rien tant qu'on
 *     ignore que ce sont les carrières ;
 *   - **64 des 152 personnes n'ont aucune faction**, et la ligne disparaissait
 *     alors entièrement. Impossible de distinguer « il n'appartient à personne »
 *     de « on a oublié de le dire ». Sans attache est une information, et une
 *     information utile : personne ne montera ni ne descendra pour lui.
 */
function Appartenance({ p }: { p: PersonneDTO }) {
  if (!p.faction) return <span className="sans-attache">sans attache</span>
  return (
    <span>
      {p.faction}
      {p.factionMetier && <i> · {p.factionMetier}</i>}
    </span>
  )
}

const NOMS_CLASSE: Record<string, string> = {
  guerrier: 'guerrier',
  gardien: 'gardien',
  rogue: 'rogue',
  eclaireur: 'éclaireur',
  mage: 'mage',
  oracle: 'oracle',
  invocateur: 'invocateur',
  heraut: 'héraut',
  dieu: 'dieu',
}

/**
 * La classe d'une personne, quand elle en a une. Quinze sur cent cinquante-deux.
 *
 * Enzo : « ce n'est pas très réaliste, le fait qu'on croise aucune autre
 * classe. » Elles étaient là depuis le premier jour et aucune ne le montrait —
 * le même oubli que `veut`, écrit et jamais affiché.
 *
 * Rien pour les cent trente-sept autres, et **surtout pas** une mention « sans
 * classe » : la faction en a une parce que ne tenir à personne est un fait qui
 * change ce qu'on peut attendre de quelqu'un. N'avoir pas de classe est le cas
 * ordinaire — la coupe a été propre — et le cas ordinaire ne s'annonce pas.
 *
 * « Des tiens » est en revanche une information dure, et la seule certitude que
 * le joueur puisse avoir sur quelqu'un : celui-là ne l'écornera pas.
 */
function Classe({ p }: { p: PersonneDTO }) {
  if (!p.classe) return null
  const nom = NOMS_CLASSE[p.classe] ?? p.classe
  if (!p.desTiens) return <span className="classe-pnj">{nom}</span>
  return (
    <span className="classe-pnj classe-tienne" title="De ta maison : il tient parole.">
      {nom} · des tiens
    </span>
  )
}

export function Gens({ id, surChangement }: { id: string; surChangement: () => void }) {
  const [g, setG] = useState<GensDTO | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [issue, setIssue] = useState<{ qui: string; texte: string } | null>(null)
  /*
   * Le Débiteur.
   *
   * Un voyageur d'apparence ordinaire avec un paquet dans le dos, croisé depuis
   * toujours, pris pour du décor. Il parle dans les secondes qui suivent un
   * acte où l'on a payé à la place de quelqu'un, une seule fois, et il ne
   * revient pas. Aucun avertissement, aucun détail, aucun moyen d'y revenir :
   * c'est la seule obtention où l'on peut payer le prix sans recevoir l'objet.
   */
  const [debiteur, setDebiteur] = useState(false)

  const recharger = useCallback(async () => setG(await api.gens(id)), [id])
  useEffect(() => { void recharger() }, [recharger])

  async function prendre(o: OffreGensDTO) {
    setErreur(null)
    try {
      const r = await api.prendreOffre(id, o.code)
      setIssue({ qui: r.qui, texte: r.texte })
      if (r.debiteur) setDebiteur(true)
      await recharger()
      surChangement()
    } catch (e) {
      setIssue(null)
      setErreur(e instanceof ErreurApi ? e.message : "Quelque chose n'a pas abouti.")
    }
  }

  if (!g) return <div className="cadre"><div className="entete"><p className="tenu">…</p></div></div>

  const avecOffres = g.personnes.filter((p) => p.offres.length > 0)
  const sansOffres = g.personnes.filter((p) => p.offres.length === 0)

  return (
    <div className="cadre">
      <div className="entete">
        <div className="surtitre">{g.lieu}</div>
        <h1>Qui est là</h1>
        <p className="attenue" style={{ maxWidth: '62ch' }}>
          {PHRASES_ACCUEIL[g.accueil]}
        </p>

        {/*
          Pourquoi. C'est le seul endroit où les choix passés se voient
          explicitement, et il n'y a aucun chiffre : le fait, et son sens.
        */}
        {g.raisons.length > 0 && (
          <ul className="raisons">
            {g.raisons.map((r, i) => (
              <li key={i} className={r.contre ? 'contre' : 'pour'}>{r.texte}</li>
            ))}
          </ul>
        )}

        {debiteur && (
          <div className="seuil" style={{ marginTop: 18 }}>
            <p className="seuil-texte">
              Quelqu’un s’arrête à ta hauteur. Tu l’as croisé cent fois sur cette
              route sans jamais le regarder. Il porte un paquet dans le dos, il a
              l’air fatigué au-delà de ce qu’un homme peut être, et il dit :
              « Prends-le. »
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                className="action"
                onClick={() =>
                  void (async () => {
                    setDebiteur(false)
                    await api.repondreAuDebiteur(id, true).catch(() => null)
                    await recharger()
                    surChangement()
                  })()
                }
              >
                Le prendre
              </button>
              <button
                className="discret"
                onClick={() =>
                  void (async () => {
                    setDebiteur(false)
                    await api.repondreAuDebiteur(id, false).catch(() => null)
                  })()
                }
              >
                passer ton chemin
              </button>
            </div>
          </div>
        )}

        {erreur && <p className="erreur">{erreur}</p>}
        {issue && (
          <p className="note">
            <b>{issue.qui}</b> · {issue.texte}
          </p>
        )}
      </div>

      <section>
        <div className="surtitre">Ceux qui ont quelque chose à te dire</div>
        {avecOffres.length === 0 ? (
          <p className="tenu">Personne, ici, n’a rien pour toi.</p>
        ) : (
          <div className="personnes">
            {avecOffres.map((p) => (
              <div className="personne" key={p.code}>
                <div className="personne-tete">
                  <h3>{p.nom}</h3>
                  <div className="donjon-meta">
                    <span>{NOMS_DISPOSITION[p.disposition]}</span>
                    <Appartenance p={p} />
                    <Classe p={p} />
                    {/*
                      Le seul jugement que l'écran porte, et il ne vient pas de
                      nous : il vient de ce qui est déjà arrivé au joueur.
                    */}
                    {p.avise && <span className="urgence">tu le connais</span>}
                  </div>
                </div>
                <p className="personne-texte">{p.texte}</p>
                {/*
                  Ce qu'il veut, et il le dit ouvertement.

                  Rien à voir avec son intention, qui décide s'il tiendra parole
                  et qui ne sort jamais du serveur. Un homme peut vouloir qu'on
                  le laisse là et quand même vous rouler : c'est justement ce qui
                  rend le choix jouable plutôt que devinable.
                */}
                {/*
                  « Veut : », sans pronom.

                  Le DTO ne porte pas de genre, et « Il veut » s'affichait sous
                  Nore, qui tend sa chaine deux lignes plus haut au feminin. Le
                  meme defaut avait deja ete corrige sur les natures d'offre.
                  Retirer le pronom est plus juste que d'en deviner un.
                */}
                {p.veut && <p className="veut">Veut : {p.veut.toLowerCase()}</p>}

                <div className="offres-gens">
                  {p.offres.map((o) => (
                    <div className="offre-gens" key={o.code}>
                      <span className="etiquette">
                        {NOMS_NATURE[o.nature]}
                        {/*
                          Ce qui se reprend, et ce qui ne se présentera plus.
                          Ce n'est pas un avertissement sur l'offre — le canon
                          l'interdit, on ne dit jamais si elle est bonne — c'est
                          un fait de disponibilité, et les deux ne se décident
                          pas de la même façon.
                        */}
                        {o.uniqueFois ? (
                          <em className="une-seule-fois"> · une seule fois</em>
                        ) : (
                          <em className="se-reprend"> · se repropose</em>
                        )}
                      </span>
                      <p>{o.texte}</p>
                      {/*
                        Et ce que ça donne, en clair.

                        Les textes sont dits par le personnage, à sa manière, et
                        ils ne disent presque jamais ce qu'on reçoit : « reste
                        ici jusqu'au soir, et ceux qui ne figurent nulle part
                        sauront qui c'était » est une belle phrase et une
                        information nulle. On ne la réécrit pas, on met le fait à
                        côté.
                      */}
                      <p className="donne">{o.donne}</p>
                      <div className="offre-gens-pied">
                        <b>{o.prix > 0 ? `${o.prix} or` : 'rien à payer'}</b>
                        <button
                          className="action"
                          disabled={!o.abordable}
                          onClick={() => void prendre(o)}
                        >
                          {o.abordable ? 'Accepter' : 'Trop cher'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {sansOffres.length > 0 && (
        <section>
          <div className="surtitre">Les autres</div>
          <div className="personnes">
            {sansOffres.map((p) => (
              <div className="personne" key={p.code}>
                <div className="personne-tete">
                  <h3>{p.nom}</h3>
                  <div className="donjon-meta">
                    <span>{NOMS_DISPOSITION[p.disposition]}</span>
                    <Appartenance p={p} />
                    <Classe p={p} />
                    {p.avise && <span className="urgence">tu le connais</span>}
                  </div>
                </div>
                <p className="personne-texte">{p.texte}</p>
                {/*
                  Ce qu'il veut, et il le dit ouvertement.

                  Rien à voir avec son intention, qui décide s'il tiendra parole
                  et qui ne sort jamais du serveur. Un homme peut vouloir qu'on
                  le laisse là et quand même vous rouler : c'est justement ce qui
                  rend le choix jouable plutôt que devinable.
                */}
                {/*
                  « Veut : », sans pronom.

                  Le DTO ne porte pas de genre, et « Il veut » s'affichait sous
                  Nore, qui tend sa chaine deux lignes plus haut au feminin. Le
                  meme defaut avait deja ete corrige sur les natures d'offre.
                  Retirer le pronom est plus juste que d'en deviner un.
                */}
                {p.veut && <p className="veut">Veut : {p.veut.toLowerCase()}</p>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
